// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this component to implement:
 * - Lazy loading feature: Images will be loaded when they are visible in viewports
 * - Responisve images using picture element
 */

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import React from "react";
import { connect } from "react-redux";
import styled, { withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cloudinaryUrl: (getSettingsCollection(state).customSettings as any).mediaRootPath,
    // all paths are the same, we will change the width of the image using the passed cloudinaryWidth
    notFoundImagePath:
        (getSettingsCollection(state).customSettings as any).notFoundLargeImagePath ||
        (getSettingsCollection(state).customSettings as any).notFoundMediumImagePath ||
        (getSettingsCollection(state).customSettings as any).notFoundSmallImagePath,
});

export interface LazyImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<LazyImageProps>;
    /** Optional content to show while image is loading.
     * @themable */
    placeholder?: React.ReactNode;
    /** The height of the image.
     * @themable */
    height?: string;
    /** The width of the image.
     * @themable */
    width?: string;
    /** Props passed to the icon component that displays when the image fails to load.
     * @themable */
    errorIconProps?: IconPresentationProps;
    /** Props passed to the typography component that displays when the image fails to load.
     * @themable */
    errorTypographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type LazyImageProps = MobiusStyledComponentProps<
    "div",
    LazyImagePresentationProps & {
        /** Props to be passed into the inner `img` element. */
        imgProps?: JSX.IntrinsicElements["img"];
        /** The URL to fetch the image from. If not provided, LazyImage renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Callback function that will be called when the image has finished loading. */
        onLoad?: () => void;
        /** Callback function that will be called if an error occurs when loading an image. */
        onError?: () => void;

        /** <xng-change> */
        /** Props to be passed into the inner `picture` element. */
        pictureProps?: JSX.IntrinsicElements["picture"];
        /** The URL to fetch the image from for small viewports */
        smallImageSrc?: string;
        /** The URL to fetch the image from for medium viewports */
        mediumImageSrc?: string;
        /** The URL to fetch the image from for large viewports */
        largeImageSrc?: string;
        /** use This flag to disable image lazyloading */
        disableLazyLoad?: boolean;
        /** specify if using cloudinary or not */
        cloudinary?: boolean;
        /** Props passed to the Cloudinary Image URL
         * @themable */
        cloudinaryWidth?: string;
        /** </xng-change> */
        cloudinaryHeight?: string;
    }
>;

// if the image loads in under this number of milliseconds, it doesn't fade in.
const fadeInThreshold = 100;

type State = {
    src?: string;
    loaded: boolean;
    error: boolean;
    startTime: number;
    imageShouldFade: boolean;
    showPlaceholder: boolean;
    prevRatio: any;
};

const LazyImageStyle = styled.div<
    Pick<
        State & LazyImageProps,
        "error" | "imageShouldFade" | "loaded" | "cloudinary" | "cloudinaryWidth" | "cloudinaryHeight"
    >
>`
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: ${({ error, theme }) => (error ? resolveColor("common.accent", theme) : null)};
    width: ${getProp("width")};
    height: ${getProp("height")};
    overflow: hidden;
    img {
        width: ${({ cloudinary, cloudinaryWidth }) =>
            cloudinary && cloudinaryWidth ? `${cloudinaryWidth}px` : "100%"};
        height: ${({ cloudinary, cloudinaryHeight }) =>
            cloudinary && cloudinaryHeight ? `${cloudinaryHeight}px` : "100%"};
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        opacity: ${({ loaded }) => (loaded ? 1 : 0)};
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
    .LazyImage-Placeholder {
        width: 100%;
        height: 100%;
        position: absolute;
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        opacity: ${({ loaded }) => (loaded ? 0 : 1)};
    }
    p {
        text-align: center;
        display: inline-block;
        width: 100%;
        overflow-wrap: break-word;
        word-wrap: break-word;
    }
    ${injectCss}
`;

/**
 * Defers loading of an image until after its first render. If the asset takes over 100ms to load, the image is faded
 * into view.
 */

type Props = LazyImageProps & ReturnType<typeof mapStateToProps>;

class LazyImage extends React.Component<Props, State> {
    // this parameter is used to clear the timeout call on unmount
    fadeInTimeout?: NodeJS.Timeout;
    state: State = {
        loaded: false,
        error: false,
        startTime: 0,
        imageShouldFade: false,
        showPlaceholder: false,
        prevRatio: 0,
    };

    // thses two should be pulled from customSettings once they are passed down to FE
    imageFolder = "development/images";
    addtionalRemovableText = "image/upload/";

    imageRef: React.RefObject<HTMLImageElement>;
    observer: IntersectionObserver | undefined;

    constructor(props: Props) {
        super(props);
        this.imageRef = React.createRef<HTMLImageElement>();
        if (props.src) {
            this.srcWithoutQuery = this.replaceSrc(props.src);
        }
    }

    UNSAFE_componentWillUpdate(nextProps: Readonly<Props>): void {
        if (nextProps.src) {
            this.srcWithoutQuery = this.replaceSrc(nextProps.src);
        }
    }

    srcWithoutQuery?: string;

    replaceSrc(src?: string) {
        if (src) {
            return src.replace(/\?.*/, "");
        }
    }

    // keeping this function as OOTB
    loadImage = () => {
        const { cloudinary = true } = this.props;
        if (this.props.src && this.imageRef?.current) {
            this.imageRef.current.src = cloudinary ? this.getCloudinaryImageSrc(this.props.src) : this.props.src;
        }
        // if empty url was passed
        if (!this.props.src && this.imageRef?.current) {
            this.imageRef.current.src = this.getCloudinaryImageSrc();
        }
        if (this.imageRef?.current) {
            this.imageRef.current.onload = () => {
                this.setState(
                    currentState => ({
                        // fade if image takes more than 100ms to load (i.e. not cached)
                        imageShouldFade: Date.now() - currentState.startTime > fadeInThreshold,
                        loaded: true,
                        src: cloudinary ? this.getCloudinaryImageSrc(this.props.src) : this.srcWithoutQuery,
                    }),
                    this.props.onLoad,
                );
                if (this.imageRef?.current && !this.imageRef?.current.src) {
                    this.imageRef.current.src = cloudinary
                        ? this.getCloudinaryImageSrc(this.props.src)!
                        : this.srcWithoutQuery || this.getCloudinaryImageSrc();
                }
            };
            this.imageRef.current.onerror = () => {
                this.setState(
                    {
                        loaded: true,
                        error: true,
                    },
                    this.props.onError,
                );
            };
        }
    };

    // <xng-change>
    createObserver = () => {
        const options = {
            threshold: 0,
        };

        // register the image to the observer
        this.observer = new IntersectionObserver(this.handleIntersect, options);
        if (this.imageRef.current) {
            this.observer.observe(this.imageRef?.current);
        }
    };

    handleIntersect = (entries: any, observer: any) => {
        entries.forEach((entry: any) => {
            if (this.imageRef.current) {
                if ((entry.intersectionRatio > this.state.prevRatio || entry.isIntersecting) && !this.state.loaded) {
                    this.loadImage();
                    // in case the image is intersected, then remove the observer  -- VERY IMPORTANT FOR PAGE PERFORMANCE
                    observer.unobserve(this.imageRef.current);
                }
                this.setState({ prevRatio: entry.intersectionRatio });
            }
        });
    };
    // <xng-change>

    reloadImage() {
        this.setState({ startTime: Date.now(), loaded: false });
        this.createObserver();
        this.fadeInTimeout = setTimeout(() => {
            this.setState({ showPlaceholder: true });
        }, fadeInThreshold);
    }

    componentDidMount() {
        this.reloadImage();
    }

    componentWillUnmount() {
        if (this.observer && this.imageRef?.current) {
            this.observer = undefined;
        }
        
        clearTimeout(this.fadeInTimeout);
    }

    /** <xng-change> */
    hasInvalidProps() {
        return !this.props.src;
    }

    getCloudinaryImageSrc = (src?: string) => {
        const { notFoundImagePath, cloudinaryUrl, cloudinaryWidth, cloudinaryHeight } = this.props;
        let hcloudinaryHeight = "";
        if (cloudinaryHeight) {
            hcloudinaryHeight = `,h_${cloudinaryHeight}/`;
        }
        if (
            src &&
            (src.toLowerCase().indexOf("wwww.") > -1 ||
                (src.toLowerCase().indexOf("http") > -1 && src.toLowerCase().indexOf("res.") === -1) ||
                src.toLowerCase().indexOf("userfiles") > -1)
        ) {
            return src;
        }
        if (src && cloudinaryUrl) {
            const trimmedSrc1 = src.replace(cloudinaryUrl, "");
            const trimmedSrc2 = trimmedSrc1.replace(this.addtionalRemovableText, "");
            const inlcudesTransformation = trimmedSrc2.indexOf("f_") > -1 || trimmedSrc2.indexOf("w_") > -1;
            return `${cloudinaryUrl}${
                !inlcudesTransformation ? `f_auto,c_fit,w_${cloudinaryWidth || "auto"}${hcloudinaryHeight}/` : ""
            }${trimmedSrc2}`;
        }
        const notFoundSrc1 = notFoundImagePath?.replace(cloudinaryUrl, "");
        const notFoundSrc2 = notFoundSrc1?.replace(this.addtionalRemovableText, "");
        return `${cloudinaryUrl}f_auto,c_fit,w_${cloudinaryWidth || "auto"}${hcloudinaryHeight}/${notFoundSrc2}`;
    };

    /** </xng-change> */

    render() {
        const {
            mergeCss,
            src: propSrc,
            placeholder,
            imgProps,
            altText,
            disableLazyLoad,
            cloudinary = true,
            cloudinaryWidth,
            cloudinaryHeight,
            ...otherProps
        } = this.props;
        const { error, imageShouldFade, loaded, showPlaceholder, src: stateSrc } = this.state;
        const { applyStyledProp, spreadProps } = applyPropBuilder(this.props, { component: "lazyImage" });
        const resolvedMergeCss = mergeCss ?? this.props?.theme?.lazyImage?.defaultProps?.mergeCss;

        if (disableLazyLoad) {
            return (
                <LazyImageStyle
                    css={applyStyledProp("css", resolvedMergeCss)}
                    {...{ loaded: true }}
                    {...otherProps}
                    width={this.props?.width}
                >
                    <img
                        alt={altText}
                        width={this.props.width}
                        src={
                            propSrc && propSrc.indexOf("NotFound") > -1
                                ? this.getCloudinaryImageSrc()
                                : this.getCloudinaryImageSrc(propSrc)
                        }
                        {...imgProps}
                    />
                </LazyImageStyle>
            );
        }

        const baseUrl = cloudinary ? this.getCloudinaryImageSrc(propSrc) : loaded && !error ? stateSrc : propSrc;

        return (
            <LazyImageStyle
                css={applyStyledProp("css", resolvedMergeCss)}
                {...{ error, imageShouldFade, loaded, cloudinary, cloudinaryWidth, cloudinaryHeight }}
                {...otherProps}
                width={this.props?.width || `${cloudinaryWidth ? `${cloudinaryWidth}px` : ""}`}
                height={this.props?.height || `${cloudinaryHeight ? `${cloudinaryHeight}px` : ""}`}
            >
                {showPlaceholder && <span className="LazyImage-Placeholder">{placeholder}</span>}
                {
                    !error && (
                        /** <xng-change> */ <img
                            ref={this.imageRef}
                            width={`${cloudinaryWidth ? `${cloudinaryWidth}px` : ""}`}
                            height={`${cloudinaryHeight ? `${cloudinaryHeight}px` : ""}`}
                            alt={altText}
                            data-src={baseUrl}
                            {...imgProps}
                        />
                    ) /** </xng-change> */
                }
                {(loaded && error) || error ? (
                    cloudinary ? (
                        <img alt={altText} src={this.getCloudinaryImageSrc()} {...imgProps} />
                    ) : (
                        <Typography className="LazyImage-ErrorMessage" {...spreadProps("errorTypographyProps")} as="p">
                            {altText}
                        </Typography>
                    )
                ) : null}
            </LazyImageStyle>
        );
    }

    static defaultProps = {
        height: "100%",
        width: "auto",
    };
}

/** @component */
export default connect(mapStateToProps)(withTheme(LazyImage));
